/**
 * Pure helpers that turn the backend's flat list of available time slots into
 * the day/time structure the "When would you like it?" picker renders.
 *
 * `availableTimeSlots` is the same shape the order loader provides: an array of
 * ISO strings for bookable slots, plus a literal `null` entry when ASAP is
 * available. These helpers never mix ASAP into the scheduled times — ASAP is a
 * separate mode in the new picker, defined once.
 */
import { format, isSameDay, startOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";

export type ScheduleDay = {
  /** Start-of-day Date (in the site's zone) used as the selection key. */
  date: Date;
  /** "Today" or a short weekday, e.g. "Thu". */
  weekday: string;
  /** Day of month, e.g. "28". */
  dayOfMonth: string;
};

export type TimeOption = {
  /** The original ISO slot string to save. */
  iso: string;
  /** Display label in the site's zone, e.g. "11:30 PM". */
  label: string;
};

/** Whether ASAP is offered (a `null` entry in the slot list). */
export function hasAsap(slots: (string | null)[] | undefined): boolean {
  return !!slots?.includes(null);
}

const zoned = (slots: (string | null)[] | undefined, timeZone: string) =>
  (slots ?? []).filter((s): s is string => s !== null).map((s) => ({
    iso: s,
    z: toZonedTime(s, timeZone),
  }));

/**
 * The distinct days (in the site's zone) that have at least one scheduled slot,
 * ascending. "Today" is labelled relative to the site's current day.
 */
export function scheduleDays(
  slots: (string | null)[] | undefined,
  timeZone: string,
): ScheduleDay[] {
  const z = zoned(slots, timeZone);
  const byDay = new Map<number, Date>();
  for (const { z: d } of z) {
    const key = startOfDay(d).getTime();
    if (!byDay.has(key)) byDay.set(key, startOfDay(d));
  }

  const todayKey = startOfDay(toZonedTime(new Date(), timeZone)).getTime();

  return Array.from(byDay.values())
    .sort((a, b) => a.getTime() - b.getTime())
    .map((date) => ({
      date,
      weekday: date.getTime() === todayKey ? "Today" : format(date, "eee"),
      dayOfMonth: format(date, "d"),
    }));
}

/**
 * The scheduled time options for a given day (in the site's zone), ascending.
 * Pass the `date` from a ScheduleDay.
 */
export function timesForDay(
  slots: (string | null)[] | undefined,
  timeZone: string,
  day: Date,
): TimeOption[] {
  return zoned(slots, timeZone)
    .filter(({ z }) => isSameDay(z, day))
    .sort((a, b) => a.z.getTime() - b.z.getTime())
    .map(({ iso, z }) => ({ iso, label: format(z, "hh:mm a") }));
}
